import React, { useState, useEffect } from 'react';
import { get, post, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function ConversionRules() {
  const { user } = useAuth();
  const canWrite = ['super_admin','admin'].includes(user?.role);
  const canView = ['super_admin','admin','inventory_officer','production_team_lead'].includes(user?.role);

  const [rules, setRules] = useState([]);
  const [skus, setSkus] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      get('/inventory-control/conversion-rules'),
      get('/inventory-control/skus')
    ]).then(([r, s]) => {
      setRules(r.data || []);
      setSkus(s.data || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    get('/materials')
      .then(response => {
        setMaterials(
          (response.data || [])
            .filter(
              material =>
                material.is_active !== false
            )
        );
      })
      .catch(() => {
        setMaterials([]);
      });
  }, []);

  const activeMaterials =
    materials.filter(
      material =>
        material.is_active !== false
    );

  const destinationSKUs =
    skus.filter(
      sku =>
        sku.inventory_type ===
          'prepared' &&
        sku.is_active !== false &&
        sku.material_id ===
          modal?.source_material_id
    );

  const filtered = rules.filter(r =>
    !search ||
    r.source_description?.toLowerCase().includes(search.toLowerCase()) ||
    r.dest_description?.toLowerCase().includes(search.toLowerCase()) ||
    r.source_sku?.toLowerCase().includes(search.toLowerCase()) ||
    r.dest_sku?.toLowerCase().includes(search.toLowerCase())
  );

  const openModal = (rule=null) => {
    setError('');
    setModal(rule ? { ...rule } : {
      source_material_id:'', destination_sku_id:'',
      expected_yield:'', cut_specification:'',
      allowable_waste_percent:0, conversion_instructions:'',
      is_active:true
    });
  };

  const save = async () => {
    setError('');
    if (!modal.source_material_id || !modal.destination_sku_id || !modal.expected_yield) {
      setError('Parent material, destination SKU and expected yield are required'); return;
    }
    if (modal.source_material_id === modal.destination_sku_id) {
      setError('The destination child SKU must belong to the selected parent material'); return;
    }
    setSaving(true);
    try {
      if (modal.id) {
        await put('/inventory-control/conversion-rules/'+modal.id, modal);
      } else {
        await post('/inventory-control/conversion-rules', modal);
      }
      setModal(null);
      load();
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

  // Calculate output estimate
  const calcOutput = (rule) => {
    if (!rule.expected_yield) return null;
    const yield_per = parseFloat(rule.expected_yield);
    const waste = parseFloat(rule.allowable_waste_percent||0);
    const net = yield_per * (1 - waste/100);
    return { gross: yield_per, waste_pct: waste, net: net.toFixed(1) };
  };

  if (!canView) return (
    <div className="empty-state"><h3>Access restricted</h3></div>
  );

  return (
    <div>
      <p>Measured cutting rules calculate whole pieces from the full batch. Any per-unit illustration is a reference, not a quantity to multiply by yards or rolls. Reusable offcuts are separate from waste.</p>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Conversion Rules</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Define how parent materials (rolls, yards) convert into prepared production units (pieces, sets)
          </p>
        </div>
        {canWrite && (
          <button className="btn btn-primary" onClick={()=>openModal()}>+ Add Rule</button>
        )}
      </div>

      {/* Info banner */}
      <div style={{background:'#EDF4FF',border:'1px solid #B0C8F0',borderRadius:8,
        padding:'10px 16px',marginBottom:20,fontSize:12,color:'#1E4FAD'}}>
        💡 Conversion rules define how many prepared pieces are expected from one raw unit.
        For example: <strong>1 Black Webbing Roll → 300 pieces at 26 inches each</strong>.
        BloansBooks uses these to calculate parent-material requirements when prepared stock is insufficient.
      </div>

      {/* Search */}
      <div className="search-bar" style={{marginBottom:16}}>
        <input placeholder="Search by SKU or description..."
          value={search} onChange={e=>setSearch(e.target.value)} style={{flex:1}} />
        <button className="btn btn-outline btn-sm" onClick={load}>↻ Refresh</button>
      </div>

      {/* Table */}
      <div className="tbl-wrap">
        {loading ? <div className="spinner"/> : (
          <table>
            <thead><tr style={{background:'#3B2A21'}}>
              <th style={{color:'#F2EFE7'}}>Source (Raw)</th>
              <th style={{color:'#F2EFE7'}}>Destination (Prepared)</th>
              <th style={{color:'#F2EFE7'}}>Cut Specification</th>
              <th style={{textAlign:'right',color:'#F2EFE7'}}>Yield / Unit</th>
              <th style={{textAlign:'right',color:'#F2EFE7'}}>Waste %</th>
              <th style={{textAlign:'right',color:'#F2EFE7'}}>Net Yield</th>
              <th style={{color:'#F2EFE7'}}>Status</th>
              {canWrite && <th></th>}
            </tr></thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="8">
                  <div className="empty-state">
                    <h3>No conversion rules yet</h3>
                    <p>Add rules to define how rolls and yards convert into production pieces</p>
                    {canWrite && (
                      <button className="btn btn-primary" onClick={()=>openModal()}>
                        Add First Rule
                      </button>
                    )}
                  </div>
                </td></tr>
              ) : filtered.map(r => {
                const calc = calcOutput(r);
                return (
                  <tr key={r.id} style={{opacity:r.is_active?1:0.5}}>
                    <td>
                      <div style={{fontWeight:700,fontSize:12,fontFamily:'monospace',color:'#1E4FAD'}}>
                        {r.source_sku}
                      </div>
                      <div style={{fontSize:12,color:'#3B2A21',marginTop:2}}>{r.source_description}</div>
                      <div style={{fontSize:10,color:'#8A8A8A'}}>{r.source_unit}</div>
                    </td>
                    <td>
                      <div style={{fontWeight:700,fontSize:12,fontFamily:'monospace',color:'#2E6B3A'}}>
                        {r.dest_sku}
                      </div>
                      <div style={{fontSize:12,color:'#3B2A21',marginTop:2}}>{r.dest_description}</div>
                      <div style={{fontSize:10,color:'#8A8A8A'}}>{r.dest_unit}</div>
                    </td>
                    <td style={{fontSize:12,color:'#555'}}>
                      {r.cut_specification || '—'}
                    </td>
                    <td className="amt" style={{fontWeight:700,color:'#3B2A21'}}>
                      {r.cutting_config ? (r.cutting_config.type==='benchmark'?'10 per roll benchmark':'Batch calculation') : Number(r.expected_yield).toLocaleString()} {r.cutting_config ? '' : r.dest_unit}
                    </td>
                    <td className="amt" style={{color:'#8B5E0A'}}>
                      {r.allowable_waste_percent > 0 ? r.allowable_waste_percent+'%' : '—'}
                    </td>
                    <td className="amt" style={{color:'#2E6B3A',fontWeight:700}}>
                      {calc ? Number(calc.net).toLocaleString()+' '+r.dest_unit : '—'}
                    </td>
                    <td>
                      <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                        background:r.is_active?'#E8F5EB':'#F2EFE7',
                        color:r.is_active?'#2E6B3A':'#8A8A8A'}}>
                        {r.is_active?'Active':'Inactive'}
                      </span>
                    </td>
                    {canWrite && (
                      <td>
                        {r.cutting_config ? <span>Yield rule configured</span> : <button className="btn btn-ghost btn-sm" onClick={()=>openModal(r)}>Edit</button>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setModal(null)}>
          <div className="modal" style={{maxWidth:580}}>
            <div className="modal-hdr">
              <span className="modal-title">{modal.id ? 'Edit Conversion Rule' : 'Add Conversion Rule'}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}

              <div style={{background:'#F9F8F5',borderRadius:8,padding:12,marginBottom:16,fontSize:12,color:'#555'}}>
                Select the <strong>parent raw material</strong> (roll, yard) as the source,
                and the <strong>prepared material SKU</strong> (piece, set) as the destination.
                Then set how many prepared units are expected from one raw unit.
              </div>

              <div className="form-group">
                <label className="form-label">Parent Material (Raw Stock) *</label>
                <select className="form-control" value={modal.source_material_id||''}
                  onChange={e=>setModal({
                    ...modal,
                    source_material_id:
                      e.target.value,
                    destination_sku_id:''
                  })}>
                  <option value="">Select parent material...</option>
                  {activeMaterials.map(s=>(
                    <option key={s.id} value={s.id}>
                      {s.material_code} — {s.name} ({s.unit_of_measure})
                    </option>
                  ))}
                </select>
                {activeMaterials.length === 0 && (
                  <div className="form-hint" style={{color:'#8F2E24'}}>
                    No raw SKUs found — add raw material SKUs first
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Destination SKU (Prepared Material) *</label>
                <select className="form-control" value={modal.destination_sku_id||''}
                  onChange={e=>setModal({...modal,destination_sku_id:e.target.value})}>
                  <option value="">Select destination child SKU...</option>
                  {destinationSKUs.map(s=>(
                    <option key={s.id} value={s.id}>
                      {s.sku} — {s.description} ({s.unit_of_measure})
                    </option>
                  ))}
                </select>
                {destinationSKUs.length === 0 && (
                  <div className="form-hint" style={{color:'#8F2E24'}}>
                    No destination child SKU exists for the selected parent material
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Expected Yield per Raw Unit *</label>
                  <input type="number" className="form-control" value={modal.expected_yield||''}
                    onChange={e=>setModal({...modal,expected_yield:e.target.value})}
                    placeholder="e.g. 300" min="0" step="0.01" />
                  <div className="form-hint">
                    How many prepared pieces from 1 roll/yard
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Allowable Waste %</label>
                  <input type="number" className="form-control" value={modal.allowable_waste_percent||0}
                    onChange={e=>setModal({...modal,allowable_waste_percent:e.target.value})}
                    placeholder="e.g. 2" min="0" max="100" step="0.1" />
                  <div className="form-hint">
                    Expected cutting waste percentage
                  </div>
                </div>
              </div>

              {modal.expected_yield && (
                <div style={{background:'#E8F5EB',borderRadius:8,padding:'10px 14px',
                  marginBottom:14,fontSize:12,color:'#2E6B3A'}}>
                  📊 Expected output: <strong>{Number(modal.expected_yield).toLocaleString()}</strong> pieces gross,&nbsp;
                  <strong>{(modal.expected_yield * (1-(modal.allowable_waste_percent||0)/100)).toFixed(1)}</strong> pieces net after {modal.allowable_waste_percent||0}% waste
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Cut Specification</label>
                <input className="form-control" value={modal.cut_specification||''}
                  onChange={e=>setModal({...modal,cut_specification:e.target.value})}
                  placeholder="e.g. 26 inches per piece, 2cm overlap allowed" />
              </div>

              <div className="form-group">
                <label className="form-label">Conversion Instructions</label>
                <textarea className="form-control" rows={3} value={modal.conversion_instructions||''}
                  onChange={e=>setModal({...modal,conversion_instructions:e.target.value})}
                  placeholder="Any specific instructions for the production team during conversion..." />
              </div>

              {modal.id && (
                <div className="form-group">
                  <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                    <input type="checkbox" checked={modal.is_active!==false}
                      onChange={e=>setModal({...modal,is_active:e.target.checked})}
                      style={{accentColor:'#556B2F'}} />
                    <span style={{fontSize:13}}>Rule is active</span>
                  </label>
                </div>
              )}
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving?'Saving...':modal.id?'Save Changes':'Add Rule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
